import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    Button,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    InitialsAvatar,
    useTheme,
} from "@elit-hair/elit-ui";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronRight, TbLogout, TbMoon, TbSettings, TbSun } from "react-icons/tb";

import { cn } from "@/lib/utils";
import { useCall } from "@/providers/hooks/use-call";

import { Link } from "../link";
import { type NavItem, collectNavTree } from "../sidebar/navigation-items";
import { checkIsActive } from "../sidebar/sidebar-menu-nav-item.utils";

export type MobileMenuProps = {
    open: boolean;
    onClose: () => void;
};

type MobileNavItemProps = {
    item: NavItem;
    onClose: () => void;
};

function MobileNavItem({ item, onClose }: MobileNavItemProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const { userData } = useAuth();

    if (!item.title || !item.icon) return null;

    const filteredChildren = (item.children ?? []).filter((child) =>
        checkPermissions(userData?.permissionCodes, child.permissions, child.permissionMode)
    );
    const hasChildren = filteredChildren.length > 0;
    const currentPath = location.pathname;
    const childActive = filteredChildren.some((child) =>
        checkIsActive({ pathname: child.to ?? "", currentPathname: currentPath })
    );
    const isCurrent = !hasChildren && checkIsActive({ pathname: item.to ?? "", currentPathname: currentPath });
    const Icon = item.icon as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;

    const buttonClassName = (active: boolean) =>
        cn(
            "w-full flex items-center justify-start gap-2 px-2 py-2 rounded-lg",
            active
                ? "bg-foreground text-background hover:bg-foreground/90"
                : "text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        );

    if (!hasChildren) {
        return (
            <Button
                variant={isCurrent ? "default" : "ghost"}
                className={buttonClassName(isCurrent)}
                asChild
                onClick={onClose}
            >
                <Link to={item.to ?? "/"} className="flex items-center gap-2 w-full">
                    <Icon size={20} style={{ color: "inherit", flexShrink: 0 }} />
                    <span className="text-md font-medium ml-2">{t(item.title)}</span>
                </Link>
            </Button>
        );
    }

    return (
        <Collapsible defaultOpen={childActive} className="group/collapsible">
            <CollapsibleTrigger asChild>
                <Button variant={childActive ? "default" : "ghost"} className={buttonClassName(childActive)}>
                    <Icon size={20} style={{ color: "inherit", flexShrink: 0 }} />
                    <span className="text-md font-medium ml-2">{t(item.title)}</span>
                    <TbChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="flex flex-col gap-1 pl-4 mt-1">
                    {filteredChildren.map((child) => {
                        if (!child.title) return null;
                        const isChildCurrent = checkIsActive({
                            pathname: child.to ?? "",
                            currentPathname: currentPath,
                        });
                        const ChildIcon = child.icon as React.ComponentType<{
                            size?: number;
                            style?: React.CSSProperties;
                        }>;

                        return (
                            <Button
                                key={child.id}
                                variant={isChildCurrent ? "default" : "ghost"}
                                className={buttonClassName(isChildCurrent)}
                                asChild
                                onClick={onClose}
                            >
                                <Link to={child.to ?? "/"} className="flex items-center gap-2 w-full">
                                    {ChildIcon && <ChildIcon size={18} style={{ color: "inherit", flexShrink: 0 }} />}
                                    <span className="text-sm font-medium ml-2">{t(child.title)}</span>
                                </Link>
                            </Button>
                        );
                    })}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { isCallActive } = useCall();
    const drawerRef = useRef<HTMLDivElement>(null);
    const [show, setShow] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);

    const items = useMemo(() => collectNavTree(), []);

    // Mount for exit animation
    useEffect(() => {
        if (open) {
            setShow(true);
        } else {
            const timeout = setTimeout(() => setShow(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [open]);

    // Animation-Trigger after mount
    useEffect(() => {
        if (show && open) {
            setShouldAnimate(false);
            const timeout = setTimeout(() => setShouldAnimate(true), 10);
            return () => clearTimeout(timeout);
        }
    }, [show, open]);

    // Close on ESC
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-mobileMenuOverlay flex h-dvh">
            {/* Overlay with fade animation */}
            <div
                className={cn(
                    "bg-black/40 flex-1 transition-opacity duration-300",
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={onClose}
            />
            {/* Drawer with slide-in/out animation */}
            <nav
                ref={drawerRef}
                className={cn(
                    "bg-background w-64 h-full shadow-lg p-4 flex flex-col transition-transform duration-300 ease-in-out focus:outline-none",
                    shouldAnimate && open ? "translate-x-0" : "translate-x-full",
                    "fixed right-0 top-0 bottom-0 z-mobileMenu"
                )}
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
            >
                {/* User Profile */}
                <div className="flex items-center gap-3 mb-6">
                    <InitialsAvatar name={userData?.name || ""} size="md" />
                    <div className="flex flex-col">
                        <span className="font-semibold text-base text-ellipsis overflow-hidden whitespace-nowrap max-w-[120px]">
                            {userData?.name}
                        </span>
                        <span className="text-xs text-muted-foreground">{t("profile")}</span>
                    </div>
                </div>
                {/* Navigation */}
                <div className="flex flex-col gap-1 flex-1">
                    {items.map((item) => {
                        if (!checkPermissions(userData?.permissionCodes, item.permissions, item.permissionMode)) {
                            return null;
                        }

                        return <MobileNavItem key={item.id} item={item} onClose={onClose} />;
                    })}
                </div>
                {/* Settings & Theme */}
                <div className="mt-6 flex flex-col gap-2">
                    <Button variant="ghost" className="w-full flex items-center gap-2" asChild onClick={onClose}>
                        <Link to="/settings" className="flex items-center gap-2 w-full">
                            <TbSettings size={20} />
                            <span>{t("settings")}</span>
                        </Link>
                    </Button>
                    <Button variant="ghost" className="w-full flex items-center gap-2" onClick={toggleTheme}>
                        {theme === "dark" ? <TbSun size={20} /> : <TbMoon size={20} />}
                        <span>{theme === "dark" ? t("light_mode") : t("dark_mode")}</span>
                    </Button>
                    <Button
                        variant="ghost"
                        className="w-full flex items-center gap-2 text-red-500 hover:bg-red-500/20 hover:text-red-600"
                        onClick={() => {
                            handleLogout();
                            onClose();
                        }}
                        disabled={isCallActive()}
                    >
                        <TbLogout size={20} />
                        <span>{t("logout")}</span>
                    </Button>
                </div>
            </nav>
        </div>
    );
}
